import type { CurrencyAmount, IsoDate } from '@omnipresentgroup/design-system';

import type { Country } from '../common';

export enum FileFormat {
  CSV = 'csv',
  PDF = 'pdf',
}

export enum InvoiceReportStatus {
  FULLY_APPLIED = 'FULLY_APPLIED',
  OPEN = 'OPEN',
  PAID_IN_FULL = 'PAID_IN_FULL',
  NOT_DEPOSITED = 'NOT_DEPOSITED',
  DEPOSITED = 'DEPOSITED',
  VOIDED = 'VOIDED',
}

export enum InvoiceReportNetSuiteType {
  INVOICE = 'INVOICE',
  CREDIT = 'CREDIT_NOTE',
  PAYMENT = 'PAYMENT',
  REFUND = 'REFUND',
}

export enum InvoiceReportType {
  PAYROLL = 'PAYROLL',
  DEPOSIT = 'DEPOSIT',
  CASH_RECEIPTS = 'CASH_RECEIPTS',
  SETUP_FEE = 'SETUP_FEE',
  MISC = 'MISC',
  SALARY_PREPAYMENT = 'SALARY_PREPAYMENT',
  JOURNAL = 'JOURNAL',
  COST_RECHARGE_INVOICE = 'COST_RECHARGE_INVOICE',
  SALARY_BONUS_PREFUNDING = 'SALARY_BONUS_PREFUNDING',
  MANAGEMENT_FEE = 'MANAGEMENT_FEE',
  MANAGEMENT = 'MANAGEMENT',
  FEE = 'FEE',
  LATE_PAYMENT_INTEREST = 'LATE_PAYMENT_INTEREST',
  FX_FEES = 'FX_FEES',
  CUSTOMER_REFUND = 'CUSTOMER_REFUND',
}

export enum InvoiceSyncStatusType {
  SUCCESS = 'SUCCESS',
  ABORTED = 'ABORTED',
}

export enum InvoiceSyncStatusTarget {
  XERO = 'XERO',
}

export enum SyncAbortedStatusReasonType {
  ORGANISATION_MAPPING_NOT_FOUND = 'ORGANISATION_MAPPING_NOT_FOUND',
  CONTACT_NOT_ACTIVE = 'CONTACT_NOT_ACTIVE',
  CATEGORY_MAPPINGS_MISSING = 'CATEGORY_MAPPINGS_MISSING',
  TAX_MISMATCH = 'TAX_MISMATCH',
  INVOICE_NUMBER_ALREADY_EXISTS = 'INVOICE_NUMBER_ALREADY_EXISTS',
  ACCOUNT_NOT_VALID_FOR_DOCUMENT = 'ACCOUNT_NOT_VALID_FOR_DOCUMENT',
  ACCOUNT_ARCHIVED = 'ACCOUNT_ARCHIVED',
  UNKNOWN = 'ERROR',
}

export type SyncAbortedStatusReasonEntity = {
  id: string;
  details?: {
    name: string;
    url?: string;
    code?: string;
  };
};

type OrganisationMappingNotFoundReason = {
  type: SyncAbortedStatusReasonType.ORGANISATION_MAPPING_NOT_FOUND;
};

type ContactNotActiveReason = {
  type: SyncAbortedStatusReasonType.CONTACT_NOT_ACTIVE;
  xeroContact?: SyncAbortedStatusReasonEntity;
  xeroOrganisation?: SyncAbortedStatusReasonEntity;
};

type CategoryMappingsMissingReason = {
  type: SyncAbortedStatusReasonType.CATEGORY_MAPPINGS_MISSING;
  categories?: SyncAbortedStatusReasonEntity[];
  xeroOrganisation?: SyncAbortedStatusReasonEntity;
};

type TaxMismatchReason = {
  type: SyncAbortedStatusReasonType.TAX_MISMATCH;
};

type InvoiceNumberAlreadyExistsReason = {
  type: SyncAbortedStatusReasonType.INVOICE_NUMBER_ALREADY_EXISTS;
  xeroOrganisation?: SyncAbortedStatusReasonEntity;
};

type AccountNotValidForDocumentReason = {
  type: SyncAbortedStatusReasonType.ACCOUNT_NOT_VALID_FOR_DOCUMENT;
  xeroAccount?: SyncAbortedStatusReasonEntity;
  xeroOrganisation?: SyncAbortedStatusReasonEntity;
};

type AccountArchivedReason = {
  type: SyncAbortedStatusReasonType.ACCOUNT_ARCHIVED;
  xeroAccount?: SyncAbortedStatusReasonEntity;
  xeroOrganisation?: SyncAbortedStatusReasonEntity;
};

type UnknownErrorReason = {
  type: SyncAbortedStatusReasonType.UNKNOWN;
};

export type InvoiceSyncAbortedReason =
  | OrganisationMappingNotFoundReason
  | ContactNotActiveReason
  | CategoryMappingsMissingReason
  | InvoiceNumberAlreadyExistsReason
  | AccountNotValidForDocumentReason
  | AccountArchivedReason
  | TaxMismatchReason
  | UnknownErrorReason;

export type InvoiceSyncStatus = {
  status: InvoiceSyncStatusType;
  syncAttemptedAt: IsoDate;
  syncTarget: InvoiceSyncStatusTarget;
  xeroFinancialDocument?: {
    id: string;
    details?: {
      url: string;
    };
  };
  reason?: InvoiceSyncAbortedReason;
};

export type InvoiceReport = {
  id: string;
  reference: string;
  dueDate: IsoDate;
  issueDate: IsoDate;
  companyId: string;
  totalAmount: CurrencyAmount;
  totalAmountExcludingFees: CurrencyAmount;
  status: InvoiceReportStatus;
  type: InvoiceReportNetSuiteType;
  parentTransactionId?: string;
  employeeCount: number;
  invoiceType?: InvoiceReportType;
  approved: boolean | null;
  invoicingEmails: string[];
  syncStatuses?: InvoiceSyncStatus[];
};

export type InvoiceReportEmployeeBillingInfo = {
  id: string;
  payPeriod: IsoDate;
  totalAmount: CurrencyAmount;
};

export type InvoiceReportEmployee = {
  id: string;
  fullName: string;
  email: string;
  salary?: CurrencyAmount;
  firstPayDate?: IsoDate;
  lastPayDate?: IsoDate;
  country: Country;
  role?: string;
  totalPrevInvoice?: CurrencyAmount;
  billedAmount?: CurrencyAmount;
  billedAmountExcludingFees?: CurrencyAmount;
  localAmountExcludingFees?: CurrencyAmount;
  billingNotes?: string;
  prevInvoices?: Array<Omit<InvoiceReportEmployeeBillingInfo, 'totalAmount'>>;
};

export type InvoiceReportEmployeeBreakdownItem = {
  label: string;
  amount: number;
  compared?: {
    label: string;
    amount: number;
  };
  items: InvoiceReportEmployeeBreakdownItem[];
};

export type InvoiceReportEmployeeBillingBreakdown =
  InvoiceReportEmployeeBillingInfo & {
    items: InvoiceReportEmployeeBreakdownItem[];
    comparedInvoice?: InvoiceReportEmployeeBillingInfo;
  };
